import { useEffect, useRef } from "react";
import Container from "Components/Container/Container";
import statistics from "Constant/statistics.js";

const Statistics = ({ data }) => {
  const sectionRef = useRef(null);
  const numberRefs = useRef([]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2, // Trigger when 20% of the section is visible
    };

    const handleIntersection = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          numberRefs.current.forEach((numRef, index) => {
            const { num } = data[index]; // Use `data` instead of `statistics`
            const hasPlusSign = num.includes("+");
            const hasKSign = num.includes("K") || num.includes("k");

            // Debugging: Check the value of num being passed
            console.log(`Animating ${num} for index ${index}`);

            // Remove the "+" sign for animation, but keep it for display
            const cleanNum = num.replace(/[^\dKk]/g, ""); // Keep only digits and "K/k"

            let value = parseInt(cleanNum, 10);

            if (hasKSign) {
              value *= 1000; // Handle 'K' as thousands
            }

            animateValue(numRef, 0, value, 2000, hasPlusSign, hasKSign);
          });
          observer.unobserve(entry.target); // Stop observing after the animation is triggered
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, options);
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [data]); // Use `data` in the dependency array for useEffect

  const animateValue = (ref, start, end, duration, hasPlusSign, hasKSign) => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      let value = Math.floor(progress * (end - start) + start);

      // If there's a "K", adjust the value accordingly
      if (hasKSign) {
        value = Math.floor(value / 1000) + "K";
      }

      ref.innerHTML = value + (hasPlusSign ? "+" : "");

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  };

  return (
    <section
      className="normal-section flex items-center justify-center"
      ref={sectionRef}
    >
      <Container>
        <div className="flex flex-col gap-y-6 my-10 lg:flex-row lg:my-0">
          {data?.map(({ num, text }, index) => (
            <div
              className="flex-1 flex flex-col gap-y-4 text-center"
              key={index}
            >
              <p
                ref={(el) => (numberRefs.current[index] = el)}
                className="text-5xl lg:text-7xl font-bold text-font"
              >
                {num} {/* Default display of num */}
              </p>
              <p className="text-2xl font-medium text-white capitalize">
                {text}
              </p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Statistics;
